import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { UpdateStatus } from '@garage/shared'
import { Button, ConfirmDialog, useSnackbar } from '@/ui'
import { api } from '@/lib/api.js'
import { ApiError } from '@/lib/api.js'
import { useState } from 'react'

/**
 * Update the app from Settings, for a workshop with nobody who uses git.
 *
 * The button does not perform the update. It cannot: this is a container, and
 * updating means `docker compose build` on the Windows host. What it does is
 * write a request into the data folder, which is a bind mount, where a
 * scheduled task on the host notices it within a minute and runs the real
 * update — backup first, then download, rebuild, restart.
 *
 * That indirection is invisible to the user and is the whole reason this panel
 * exists, so the states it can be in are worth spelling out rather than
 * collapsing into a spinner:
 *
 *   no helper   — the button would do nothing, so it is not offered
 *   no check    — GitHub unreachable; show the running version and say why
 *   up to date  — say so plainly
 *   available   — offer the button, with the commit and how old it is
 *   requested   — the click landed but the host has not looked yet
 *   running     — the host is working; the app may restart under us
 *   failed      — show what the host said, do not hide it
 */
export function SoftwareUpdatePanel() {
  const [confirming, setConfirming] = useState(false)
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const { t, i18n } = useTranslation()

  const { data, isLoading } = useQuery({
    queryKey: ['update-status'],
    queryFn: () => api.update.status(),
    // Poll quickly only while something is happening. The rest of the time
    // this is a settings page nobody is watching.
    refetchInterval: (query) => {
      const state = (query.state.data as UpdateStatus | undefined)?.job.state
      return state === 'requested' || state === 'running' ? 5000 : false
    },
    // The app restarts mid-update, so failures here are expected and must not
    // turn into a red error state.
    retry: false,
  })

  const start = useMutation({
    mutationFn: () => api.update.start(),
    onSuccess: () => {
      setConfirming(false)
      snackbar.success(t('softwareUpdate.updateRequested'))
      queryClient.invalidateQueries({ queryKey: ['update-status'] })
    },
    onError: (err: Error) => {
      setConfirming(false)
      snackbar.error(err instanceof ApiError ? err.message : t('softwareUpdate.couldNotRequest'))
    },
  })

  const formatWhen = (iso: string): string => {
    const then = new Date(iso)
    if (Number.isNaN(then.getTime())) return ''

    const days = Math.floor((Date.now() - then.getTime()) / 86_400_000)
    if (days <= 0) return t('softwareUpdate.today')
    if (days === 1) return t('softwareUpdate.yesterday')
    if (days < 30) return t('softwareUpdate.daysAgo', { count: days })
    return then.toLocaleDateString(i18n.language === 'ar' ? 'ar-u-nu-latn' : undefined)
  }

  if (isLoading || !data) {
    return (
      <Panel>
        <p className="text-body-sm text-on-surface-variant">{t('softwareUpdate.checking')}</p>
      </Panel>
    )
  }

  const { running, agent, latest, updateAvailable, checkError, job } = data
  const busy = job.state === 'requested' || job.state === 'running'

  return (
    <Panel>
      <dl className="space-y-1">
        <Row label={t('softwareUpdate.running')} value={<Version commit={running.commit} at={running.builtAt} formatWhen={formatWhen} />} />
        {latest && (
          <Row label={t('softwareUpdate.available')} value={<Version commit={latest.commit} at={latest.committedAt} formatWhen={formatWhen} />} />
        )}
      </dl>

      {latest?.subject && (
        <p className="text-body-sm text-on-surface-variant mt-2">{latest.subject}</p>
      )}

      {busy ? (
        <div className="mt-4 space-y-2" role="status">
          <p className="text-body-md text-on-surface">
            {job.state === 'requested' ? t('softwareUpdate.waitingForHost') : t('softwareUpdate.updating')}
          </p>
          {job.message && (
            <p className="text-body-sm text-on-surface-variant">{job.message}</p>
          )}
          <p className="text-body-sm text-on-surface-variant">
            {t('softwareUpdate.takesThreeMinutes')}
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {job.state === 'failed' && job.message && (
            <p className="text-body-sm text-error" role="alert">
              {t('softwareUpdate.lastUpdateFailed')}: {job.message}
            </p>
          )}
          {job.state === 'succeeded' && job.toCommit && (
            <p className="text-body-sm text-on-surface-variant">
              {t('softwareUpdate.lastUpdatedTo')} <span className="ref">{job.toCommit}</span>.
            </p>
          )}

          {!agent.installed ? (
            <p className="text-body-sm text-on-surface-variant">
              {t('softwareUpdate.needsUpdaterPrefix')}{' '}
              <span className="ref">Install-CypekGarage.ps1</span> {t('softwareUpdate.needsUpdaterSuffix')}
            </p>
          ) : checkError ? (
            <p className="text-body-sm text-on-surface-variant">{checkError}</p>
          ) : updateAvailable ? (
            <>
              <Button variant="filled" onClick={() => setConfirming(true)}>
                {t('softwareUpdate.installUpdate')}
              </Button>
              <p className="text-body-sm text-on-surface-variant">
                {t('softwareUpdate.takesThreeMinutesBackup')}
              </p>
            </>
          ) : (
            <p className="text-body-sm text-on-surface-variant">{t('softwareUpdate.latestVersion')}</p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('softwareUpdate.installTitle')}
        message={t('softwareUpdate.installMessage')}
        confirmLabel={t('softwareUpdate.install')}
        onConfirm={() => start.mutate()}
        onCancel={() => setConfirming(false)}
        loading={start.isPending}
      />
    </Panel>
  )
}

function Panel({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
      <h2 className="text-title-md text-on-surface">{t('softwareUpdate.title')}</h2>
      <p className="text-body-sm text-on-surface-variant mt-0.5 mb-4">
        {t('softwareUpdate.subtitle')}
      </p>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-body-sm text-on-surface-variant">{label}</dt>
      <dd className="text-body-sm text-on-surface">{value}</dd>
    </div>
  )
}

function Version({ commit, at, formatWhen }: { commit: string; at: string; formatWhen: (iso: string) => string }) {
  const { t } = useTranslation()
  if (commit === 'unknown') {
    return <span className="text-on-surface-variant">{t('softwareUpdate.notRecorded')}</span>
  }
  return (
    <>
      <span className="ref">{commit}</span>
      {at && <span className="text-on-surface-variant"> · {formatWhen(at)}</span>}
    </>
  )
}
